import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Rating,
  TextField,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  RateReview,
  Star,
  Edit,
  Delete,
  Add,
  ThumbUp,
  Hotel,
  Restaurant,
  Spa,
  LocalLaundryService,
} from '@mui/icons-material';

const myReviews = [
  {
    id: 1,
    type: 'Booking',
    bookingId: 'BK2025',
    service: 'Suite Room',
    date: '2025-10-18',
    rating: 5,
    title: 'Excellent Stay!',
    comment: 'The suite was absolutely beautiful and the service was impeccable. Will definitely return!',
    helpful: 12,
    response: 'Thank you for your wonderful feedback! We look forward to welcoming you back.',
  },
  {
    id: 2,
    type: 'Service',
    bookingId: 'SO2025',
    service: 'Spa - Swedish Massage',
    date: '2025-11-14',
    rating: 5,
    title: 'Amazing Spa Experience',
    comment: 'The massage was incredibly relaxing. The therapist was professional and skilled.',
    helpful: 8,
    response: null,
  },
  {
    id: 3,
    type: 'Booking',
    bookingId: 'BK2024',
    service: 'Standard Room',
    date: '2025-09-08',
    rating: 4,
    title: 'Great Value',
    comment: 'Clean room, comfortable bed, and friendly staff. Good value for money.',
    helpful: 5,
    response: 'We appreciate your kind words and hope to see you again soon!',
  },
];

const pendingReviews = [
  {
    id: 'BK2026',
    type: 'Booking',
    service: 'Deluxe Room',
    checkOut: '2025-11-23',
    description: '3 nights stay',
  },
];

export default function CustomerReviews() {
  const [reviews, setReviews] = useState(myReviews);
  const [pending] = useState(pendingReviews);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleAddReview = (item) => {
    setSelectedItem(item);
    setReviewForm({ rating: 5, title: '', comment: '' });
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = () => {
    if (!reviewForm.title || !reviewForm.comment) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error',
      });
      return;
    }

    const newReview = {
      id: reviews.length + 1,
      type: selectedItem.type,
      bookingId: selectedItem.id,
      service: selectedItem.service,
      date: new Date().toISOString().split('T')[0],
      rating: reviewForm.rating,
      title: reviewForm.title,
      comment: reviewForm.comment,
      helpful: 0,
      response: null,
    };

    setReviews([newReview, ...reviews]);
    setSnackbar({
      open: true,
      message: 'Review submitted successfully!',
      severity: 'success',
    });
    setReviewDialogOpen(false);
  };

  const handleDeleteReview = (reviewId) => {
    setReviews(reviews.filter(r => r.id !== reviewId));
    setSnackbar({
      open: true,
      message: 'Review deleted successfully',
      severity: 'success',
    });
  };

  const getServiceIcon = (service) => {
    if (service.includes('Room')) return <Hotel />;
    if (service.includes('Spa')) return <Spa />;
    if (service.includes('Restaurant')) return <Restaurant />;
    if (service.includes('Laundry')) return <LocalLaundryService />;
    return <RateReview />;
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) 
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map(stars => ({
    stars,
    count: reviews.filter(r => r.rating === stars).length,
    percentage: reviews.length > 0 ? (reviews.filter(r => r.rating === stars).length / reviews.length) * 100 : 0,
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          My Reviews & Feedback
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Share your experience and help us improve our services
        </Typography>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#1976d210', borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Average Rating
              </Typography>
              <Typography variant="h2" fontWeight="bold" color="#1976d2" gutterBottom>
                {averageRating}
              </Typography>
              <Rating value={parseFloat(averageRating)} precision={0.1} readOnly size="large" />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Based on {reviews.length} reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Rating Distribution
              </Typography>
              {ratingDistribution.map((dist) => (
                <Box key={dist.stars} display="flex" alignItems="center" gap={2} mb={1}>
                  <Box display="flex" alignItems="center" width={80}>
                    <Typography variant="body2" mr={1}>{dist.stars}</Typography>
                    <Star sx={{ fontSize: 16, color: '#ffa726' }} />
                  </Box>
                  <Box flex={1}>
                    <LinearProgress
                      variant="determinate"
                      value={dist.percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#f5f5f5',
                        '& .MuiLinearProgress-bar': { bgcolor: '#ffa726' },
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" width={40}>
                    {dist.count}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {pending.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff3e0' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Pending Reviews
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Share your experience with these completed services
          </Typography>
          <Grid container spacing={2}>
            {pending.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ bgcolor: '#ed6c02' }}>
                        {getServiceIcon(item.service)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {item.service}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => handleAddReview(item)}
                    >
                      Write Review
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Typography variant="h6" fontWeight="bold" mb={2}>
        My Reviews ({reviews.length})
      </Typography>

      <Grid container spacing={3}>
        {reviews.map((review) => (
          <Grid item xs={12} key={review.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box display="flex" gap={2}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 48, height: 48 }}>
                      {getServiceIcon(review.service)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {review.title}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Rating value={review.rating} readOnly size="small" />
                        <Typography variant="body2" color="text.secondary">
                          {review.rating}.0
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1} mb={1}>
                        <Chip label={review.type} size="small" />
                        <Chip label={review.service} size="small" variant="outlined" />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(review.date).toLocaleDateString()} • Ref: {review.bookingId}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton size="small" color="error" onClick={() => handleDeleteReview(review.id)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" paragraph>
                  {review.comment}
                </Typography>

                {review.response && (
                  <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Hotel Response:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {review.response}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box display="flex" alignItems="center" gap={2}>
                  <Button size="small" startIcon={<ThumbUp />}>
                    Helpful ({review.helpful})
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <RateReview color="primary" />
            <Typography variant="h6">Write a Review</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ pt: 2 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>
                <Avatar sx={{ bgcolor: '#1976d2' }}>
                  {getServiceIcon(selectedItem.service)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedItem.service}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedItem.description}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" gutterBottom>
                Overall Rating
              </Typography>
              <Rating
                value={reviewForm.rating}
                onChange={(e, value) => setReviewForm({ ...reviewForm, rating: value })}
                size="large"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Review Title"
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                margin="normal"
                placeholder="Summarize your experience"
                required
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Your Review"
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                margin="normal"
                placeholder="Tell us about your experience..."
                required
              />

              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Your review will be visible to other guests and help us improve our services.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitReview} startIcon={<Add />}>
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
